import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchClassById, fetchClassLessons } from '../../api/classes';
import { fetchWorksheets } from '../../api/worksheets';
import { getSubjectColor } from '../../styles/colors';
import styles from './ClassDetailPage.module.css';
import UnitEditModal from './UnitEditModal';
import { StandardsCoverageContent } from '../StandardsCoverage/StandardsCoveragePage';
import LessonGenerationPanel from '../ClassOnboard/LessonGenerationPanel';

const ClassDetailPage = () => {
  const { id } = useParams();
  const [cls, setCls] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showGenerate, setShowGenerate] = useState(false);

  const loadClass = useCallback(async () => {
    try {
      setLoading(true);
      const [classRes, lessonsRes, worksheetsRes] = await Promise.all([
        fetchClassById(id),
        fetchClassLessons(id),
        fetchWorksheets({ classId: id }),
      ]);
      setCls(classRes.data);
      setLessons(lessonsRes.data || []);
      setWorksheets(worksheetsRes.data || []);
    } catch {
      setError('Failed to load class details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadClass(); }, [loadClass]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading class...</p>
        </div>
      </div>
    );
  }

  if (error || !cls) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>{error || 'Class not found.'}</div>
        <Link to="/classes" className={styles.backLink}>Back to My Classes</Link>
      </div>
    );
  }

  const color = getSubjectColor(cls.subject);
  const scheduledCount = lessons.filter(l => l.scheduledDate).length + worksheets.filter(w => w.scheduledDate).length;
  const unitCount = cls.units?.length ?? 0;

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const formatDateRange = (s, e) => `${formatDate(s)} – ${formatDate(e)}`;

  // Group lessons by unit
  const unitLessons = {};
  const unassigned = [];
  lessons.forEach(l => {
    if (l.unitId) {
      if (!unitLessons[l.unitId]) unitLessons[l.unitId] = [];
      unitLessons[l.unitId].push(l);
    } else {
      unassigned.push(l);
    }
  });

  return (
    <div className={styles.page}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <Link to="/classes">My Classes</Link>
        <span className={styles.breadcrumbSep}>/</span>
        <span>{cls.name}</span>
      </nav>

      {/* Class info header */}
      <div className={styles.infoCard} style={{ borderTopColor: color.border }}>
        <h1 className={styles.className}>{cls.name}</h1>
        <div className={styles.badges}>
          <span className={styles.badge} style={{ backgroundColor: color.bg, color: color.text }}>{cls.subject}</span>
          <span className={styles.badge}>{cls.gradeLevel}</span>
          {cls.state && <span className={styles.badge}>{cls.state}</span>}
          {cls.section && <span className={styles.badge}>Section {cls.section}</span>}
        </div>
        <p className={styles.dateRange}>{formatDateRange(cls.yearStart, cls.yearEnd)}</p>
        {cls.scheduleType && (
          <p className={styles.schedule}>{cls.scheduleType} schedule · {cls.mathMinutes} min blocks</p>
        )}
      </div>

      {/* Stats row */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{lessons.length}</span>
          <span className={styles.statLabel}>Lessons</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{worksheets.length}</span>
          <span className={styles.statLabel}>Activities</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{scheduledCount}</span>
          <span className={styles.statLabel}>Scheduled</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statValue}>{unitCount}</span>
          <span className={styles.statLabel}>Units</span>
        </div>
      </div>

      {/* Tab bar */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'lessons' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('lessons')}
        >
          Lessons
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'worksheets' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('worksheets')}
        >
          Activities
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'coverage' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('coverage')}
        >
          Standards Coverage
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Action links */}
          <div className={styles.actions}>
            <Link to={`/classes/${id}/summary`} className={styles.actionLink}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
              Print Summary
            </Link>
            <Link to="/lessons/new" className={styles.actionLink}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create Lesson
            </Link>
            {cls.units && cls.units.some(u => (unitLessons[u.id]?.length ?? 0) === 0) && (
              <button
                className={styles.actionLink}
                onClick={() => setShowGenerate(prev => !prev)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {showGenerate ? 'Hide Generation' : 'Generate Lessons'}
              </button>
            )}
          </div>

          {showGenerate && cls.units && (
            <LessonGenerationPanel classId={parseInt(id)} units={cls.units} />
          )}

          {/* Scope & Sequence */}
          {cls.units && cls.units.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Scope & Sequence</h2>
              <div className={styles.unitList}>
                {cls.units.map(unit => (
                  <div key={unit.id} className={styles.unitRow} onClick={() => setSelectedUnit(unit)} style={{ cursor: 'pointer' }}>
                    <div className={styles.unitNumber}>Unit {unit.sequenceOrder}</div>
                    <div className={styles.unitInfo}>
                      <h3 className={styles.unitTitle}>{unit.title}</h3>
                      <div className={styles.unitMeta}>
                        {unit.startDate && unit.endDate && (
                          <span>{formatDateRange(unit.startDate, unit.endDate)}</span>
                        )}
                        {unit.durationWeeks && <span>{unit.durationWeeks} weeks</span>}
                        <span>{unitLessons[unit.id]?.length ?? 0} lessons</span>
                      </div>
                      {unit.unitStandards && unit.unitStandards.length > 0 && (
                        <div className={styles.standardCodes}>
                          {unit.unitStandards.map(us => (
                            <span key={us.id} className={styles.codeChip}>{us.standard.standardCode}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {activeTab === 'lessons' && (
        <>
          {lessons.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Lessons</h2>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Date</th>
                      <th>Duration</th>
                      <th>Unit</th>
                      <th>Standards</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lessons.map(lesson => (
                      <tr key={lesson.id}>
                        <td>
                          <Link to={`/lessons/${lesson.id}/edit`} className={styles.lessonLink}>
                            {lesson.title}
                          </Link>
                        </td>
                        <td>
                          {lesson.scheduledDate
                            ? formatDate(lesson.scheduledDate)
                            : <span className={styles.draftBadge}>Draft</span>
                          }
                        </td>
                        <td>{lesson.duration ? `${lesson.duration} min` : '\u2014'}</td>
                        <td>{lesson.unit?.title || '\u2014'}</td>
                        <td>
                          {lesson.lessonStandards?.length > 0
                            ? lesson.lessonStandards.map(ls => ls.standard.standardCode).join(', ')
                            : '\u2014'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <div className={styles.emptyLessons}>
              <p>No lessons yet for this class.</p>
              <Link to="/lessons/new" className={styles.createBtn}>Create a Lesson</Link>
            </div>
          )}
        </>
      )}

      {activeTab === 'worksheets' && (
        <>
          {worksheets.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activities</h2>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Questions</th>
                      <th>Difficulty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {worksheets.map(ws => (
                      <tr key={ws.id}>
                        <td>
                          <Link to={`/worksheets/${ws.id}/edit`} className={styles.lessonLink}>
                            {ws.title}
                          </Link>
                        </td>
                        <td>
                          <span className={styles.typeBadge} data-type={ws.type}>
                            {ws.type}
                          </span>
                        </td>
                        <td>
                          {ws.scheduledDate
                            ? formatDate(ws.scheduledDate)
                            : <span className={styles.draftBadge}>Draft</span>
                          }
                        </td>
                        <td>{ws.questions?.length ?? ws._count?.questions ?? 0}</td>
                        <td>{ws.difficulty || '\u2014'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <div className={styles.emptyLessons}>
              <p>No activities yet for this class.</p>
              <Link to="/worksheets/new" className={styles.createBtn}>Create an Activity</Link>
            </div>
          )}
        </>
      )}

      {activeTab === 'coverage' && (
        <StandardsCoverageContent classId={parseInt(id)} />
      )}

      {selectedUnit && (
        <UnitEditModal
          unit={{ ...selectedUnit, class: { subject: cls.subject, gradeLevel: cls.gradeLevel } }}
          classId={parseInt(id)}
          lessons={unitLessons[selectedUnit.id] || []}
          onClose={() => setSelectedUnit(null)}
          onSaved={() => { setSelectedUnit(null); loadClass(); }}
          onDeleted={() => { setSelectedUnit(null); loadClass(); }}
        />
      )}
    </div>
  );
};

export default ClassDetailPage;
